import React, { useState } from 'react';
import { Container, Row, Col, Button, Card, Accordion } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Typewriter } from 'react-simple-typewriter';
import SEO from '../components/SEO';
import FeaturedProductList from './products/component/FeaturedProductList';
import TemplateGroupList from './products/component/TemplateGroupList';

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is Wallscapes.AI?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Wallscapes.AI is an AI-powered platform that transforms your ideas into stunning custom art for print-on-demand products. Create unique wall tapestries, posters, blankets, stickers, and more using our advanced AI art generation technology."
        }
      },
      {
        "@type": "Question",
        "name": "How does AI art generation work?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Simply describe your vision in words, and our AI technology creates unique, custom artwork based on your description. You can then order your design on various products like wall art, posters, blankets, and stickers."
        }
      },
      {
        "@type": "Question",
        "name": "What products can I customize?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We offer a wide range of customizable print-on-demand products including wall tapestries, flags, posters, canvas prints, blankets, stickers, notebooks, and more. All items feature your custom AI-generated artwork."
        }
      },
      {
        "@type": "Question",
        "name": "How long does shipping take?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Each item is custom-made to order. Production typically takes 2-5 business days, with shipping taking an additional 3-7 business days depending on your location."
        }
      },
      {
        "@type": "Question",
        "name": "Can I use the AI-generated art commercially?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "AI-generated images are owned by Wallscapes.AI and licensed for personal use only by default. Commercial use (reselling, marketing, advertising) requires a separate Commercial License. Contact us at wallscapesai@gmail.com for commercial licensing inquiries. See our license page for full details."
        }
      }
    ]
  };

  return (
    <>
      <SEO
        title="Wallscapes.AI - Create Custom AI-Generated Art for Print-on-Demand Products"
        description="Transform your ideas into stunning custom art. Design and order AI-generated wall art, posters, blankets, stickers, and more. Turn your creativity into unique print-on-demand products."
        keywords="AI art, custom wall art, print on demand, AI-generated art, custom posters, personalized blankets, custom stickers, wall tapestry, AI design, AI art generator"
        canonicalUrl="https://wallscapes.ai/"
        structuredData={faqSchema}
      />
      <Container className="mt-3">
        {/* Hero Section */}
      <Row className="align-items-center text-center mb-5">
        <Col>
          <h1 className="display-4">Transform Your Space with AI-Generated Art</h1>
          <p className="lead">
            Discover unique wall tapestries, posters, and more - or create your own.
          </p>
          <div className="d-flex flex-column flex-sm-row justify-content-center gap-3 mt-4">
            <Button variant="primary" size="lg" onClick={() => navigate('/products/create')} className="w-100 w-sm-auto">
              Create Your Own
            </Button>
            <Button variant="outline-primary" size="lg" onClick={() => navigate('/products')} className="w-100 w-sm-auto">
              Shop All Designs
            </Button>
          </div>
        </Col>
      </Row>

      {/* Template Groups */}
      <hr />
      <TemplateGroupList />

      {/* Featured Product List */}
      <hr />
      <h2 className="text-center mb-4">Popular Designs</h2>
      <p className="text-center text-muted mb-4">
        Browse our curated collection - order as-is or remix to make it your own
      </p>
      <FeaturedProductList />

      {/* Create Your Own Promo Section */}
      <Row className="text-center mb-5">
        <Col>
          <h2>AI Meets Your Imagination</h2>
          <p className="lead">
            Three easy steps: pick your product, tell us your idea, and watch it come to life.
            Because your space deserves art that's 100% original.
          </p>
          <div className="d-flex flex-column flex-md-row justify-content-center gap-2 gap-md-3">
            <Button
              variant="success"
              size="lg"
              onClick={() => navigate('/products/create')}
              className="w-100 w-md-auto py-3 py-md-2"
            >
              Create Your Own!
            </Button>
          </div>
        </Col>
      </Row>

      {/* Why Shop With Us */}
      <Row className="mb-5">
        <Col>
          <h2 className="text-center mb-4">Why Choose Wallscapes.AI?</h2>
        </Col>
      </Row>
      <Row className="text-center mb-5">
        <Col md={4} className="mb-2">
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <h3 style={{ fontSize: '1.25rem' }}>🎨 Unique AI-Generated Art</h3>
              <p>Stand out with designs you won't find anywhere else. Our AI art generator creates one-of-a-kind custom artwork tailored to your vision.</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} className="mb-2">
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <h3 style={{ fontSize: '1.25rem' }}>🚚 Fast & Reliable Shipping</h3>
              <p>Each print-on-demand item is custom-made with attention to detail and shipped directly to your door with care.</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} className="mb-2">
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <h3 style={{ fontSize: '1.25rem' }}>🧠 Create With Ease</h3>
              <p>Use our intuitive AI art generator to transform your ideas into reality. No design skills needed.</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* How It Works Section */}
      <Row className="mb-5">
        <Col>
          <h2 className="text-center mb-4">How It Works</h2>
        </Col>
      </Row>
      <Row className="mb-5">
        <Col md={4} className="mb-3 text-center">
          <h3 style={{ fontSize: '1.5rem' }}>1. Choose Your Product</h3>
          <p>Select from wall tapestries, posters, blankets, stickers, and more print-on-demand products.</p>
        </Col>
        <Col md={4} className="mb-3 text-center">
          <h3 style={{ fontSize: '1.5rem' }}>2. Describe Your Vision</h3>
          <p>Tell our AI art generator what you want to create. Be as creative and detailed as you like.</p>
        </Col>
        <Col md={4} className="mb-3 text-center">
          <h3 style={{ fontSize: '1.5rem' }}>3. Order & Enjoy</h3>
          <p>We'll create your custom art and print it on your chosen product. Fast shipping to your door.</p>
        </Col>
      </Row>

      {/* FAQ Section */}
      <Row className="mb-5">
        <Col>
          <h2 className="text-center mb-4">Frequently Asked Questions</h2>
          <Accordion>
            <Accordion.Item eventKey="0">
              <Accordion.Header>What is Wallscapes.AI?</Accordion.Header>
              <Accordion.Body>
                Wallscapes.AI is an AI-powered platform that transforms your ideas into stunning custom art for print-on-demand products. Create unique wall tapestries, posters, blankets, stickers, and more using our advanced AI art generation technology.
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="1">
              <Accordion.Header>How does AI art generation work?</Accordion.Header>
              <Accordion.Body>
                Simply describe your vision in words, and our AI technology creates unique, custom artwork based on your description. You can then order your design on various products like wall art, posters, blankets, and stickers.
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="2">
              <Accordion.Header>What products can I customize?</Accordion.Header>
              <Accordion.Body>
                We offer a wide range of customizable print-on-demand products including wall tapestries, flags, posters, canvas prints, blankets, stickers, notebooks, and more. All items feature your custom AI-generated artwork.
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="3">
              <Accordion.Header>How long does shipping take?</Accordion.Header>
              <Accordion.Body>
                Each item is custom-made to order. Production typically takes 2-5 business days, with shipping taking an additional 3-7 business days depending on your location.
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="4">
              <Accordion.Header>Can I use the AI-generated art commercially?</Accordion.Header>
              <Accordion.Body>
                AI-generated images are owned by Wallscapes.AI and licensed for personal use only by default. Commercial use (reselling, marketing, advertising) requires a separate Commercial License. Contact us at wallscapesai@gmail.com for commercial licensing inquiries. See our license page for full details.
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </Col>
      </Row>

    </Container>
    </>
  );
};

export default HomePage;
